import React from "react";

import { useController, useFormContext } from "react-hook-form";

import { Input } from "@/src/components/ui/input";
import { cn } from "@/src/lib/utils";

import type { ChangeEvent, JSX, ReactNode } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

interface InputGroupProps {
  className?: string;
  invalid?: boolean;
  children: ReactNode;
}

/**
 * Bordered row that visually merges an addon (e.g. a select) with an input into
 * a single control. The group owns the border, radius and focus ring; nested
 * pieces (`InputGroupAddon`, `InputGroupInput`) are stripped of their own.
 */
function InputGroup({
  className,
  invalid,
  children,
}: InputGroupProps): JSX.Element {
  return (
    <div
      aria-invalid={invalid ? true : undefined}
      data-slot="input-group"
      className={cn(
        "border-input focus-within:border-ring focus-within:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 flex items-stretch overflow-hidden rounded-md border shadow-xs transition-[color,box-shadow] focus-within:ring-[3px] aria-invalid:ring-[3px]",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface InputGroupAddonProps {
  className?: string;
  position?: "start" | "end";
  children: ReactNode;
}

/**
 * Slot at the start or end edge of an `InputGroup` (typically a `Select` or a
 * unit label). A nested shadcn select trigger is neutralised so it blends into
 * the group instead of drawing its own border.
 */
function InputGroupAddon({
  className,
  position = "start",
  children,
}: InputGroupAddonProps): JSX.Element {
  return (
    <div
      data-slot="input-group-addon"
      className={cn(
        "bg-muted border-input flex items-center",
        position === "start" ? "border-r" : "border-l",
        "[&_[data-slot=select-trigger]]:h-full [&_[data-slot=select-trigger]]:rounded-none [&_[data-slot=select-trigger]]:border-0 [&_[data-slot=select-trigger]]:bg-transparent [&_[data-slot=select-trigger]]:shadow-none [&_[data-slot=select-trigger]]:focus-visible:ring-0",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface InputGroupInputBaseProps {
  type?: string;
  value: number | string;
  min?: number;
  step?: number;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  autoComplete?: string;
  onBlur?: () => void;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

/** Borderless input filling the remaining width of an `InputGroup`. */
function InputGroupInputBase({
  type,
  value,
  min,
  step,
  disabled,
  placeholder,
  className,
  id,
  autoComplete,
  onBlur,
  onChange,
}: InputGroupInputBaseProps): JSX.Element {
  return (
    <Input
      autoComplete={autoComplete}
      disabled={disabled}
      id={id}
      min={min}
      onBlur={onBlur}
      onChange={onChange}
      placeholder={placeholder}
      step={step}
      type={type}
      value={value}
      className={cn(
        "rounded-none border-0 shadow-none focus-visible:ring-0",
        className,
      )}
    />
  );
}

interface InputGroupInputProps<T extends FieldValues> {
  name: FieldPathByValue<T, number | string | undefined>;
  type?: string;
  min?: number;
  step?: number;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Borderless `InputGroupInput` bound to a react-hook-form `number | string`
 * field via `useController`.
 */
function InputGroupInput<T extends FieldValues>({
  name,
  type,
  min,
  step,
  disabled,
  placeholder,
  className,
}: InputGroupInputProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field } = useController({ control, name });

  return (
    <InputGroupInputBase
      className={className}
      disabled={disabled}
      min={min}
      onChange={(event) => field.onChange(event.target.value)}
      placeholder={placeholder}
      step={step}
      type={type}
      value={field.value ?? ""}
    />
  );
}

export { InputGroup, InputGroupAddon, InputGroupInput, InputGroupInputBase };
